import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, Users, MessageCircle, Mail, Smartphone, ChevronDown, ChevronUp, Info, LayoutTemplate, CopyCheck } from 'lucide-react';
import { useContactCategories, useContactTags } from '@/features/crm/hooks/useContacts';
import { useAudiencePreview, useApprovedWhatsAppTemplates } from '../hooks/useCampaigns';
import type { ApprovedTemplate } from '../hooks/useCampaigns';
import type { Campaign, CampaignInsert, AudienceFilter, CampaignType } from '../types';
import { WHATSAPP_CHAR_LIMIT, EMAIL_CHAR_LIMIT } from '../types';

/* ─── Schema ─────────────────────────────────────────────────── */
const schema = z.object({
  name:          z.string().min(2, 'Nome deve ter ao menos 2 caracteres').max(120),
  description:   z.string().optional().nullable(),
  type:          z.enum(['whatsapp', 'email', 'sms']),
  message_body:  z.string().min(1, 'A mensagem não pode estar vazia').max(10000),
  template_vars: z.array(z.string()).default([]),
  scheduled_at:  z.string().optional().nullable(),
  legal_basis:   z.string().default('legitimate_interest'),
  // Filtros de audiência
  aud_activeOnly:   z.boolean().default(true),
  aud_categories:   z.array(z.string()).default([]),
  aud_tags:         z.array(z.string()).default([]),
  aud_neighborhood: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof schema>;

interface CampaignFormProps {
  campaign?: Campaign | null;
  onSubmit: (data: CampaignInsert) => void;
  onClose: () => void;
  isPending: boolean;
}

const TYPE_OPTIONS: { value: CampaignType; label: string; icon: React.ElementType; desc: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, desc: 'Mensagem direta via WhatsApp Business' },
  { value: 'email',    label: 'E-mail',   icon: Mail,          desc: 'Envio de e-mail para contatos cadastrados' },
  { value: 'sms',      label: 'SMS',      icon: Smartphone,    desc: 'Mensagem de texto (SMS)' },
];

const USE_CASE_LABELS: Record<string, string> = {
  checkin:           'Check-in',
  event_confirm:     'Confirmação de evento',
  material_received: 'Recebimento de material',
  prayer:            'Oração',
  leadership:        'Liderança',
  general:           'Geral',
};

export function CampaignForm({ campaign, onSubmit, onClose, isPending }: CampaignFormProps) {
  const [audienceOpen,   setAudienceOpen]   = useState(true);
  const [templatesOpen,  setTemplatesOpen]  = useState(false);
  const [appliedTpl,     setAppliedTpl]     = useState<string | null>(null);
  const { data: categories = [] } = useContactCategories();
  const { data: allTags = [] }    = useContactTags();
  const { data: waTemplates = [] } = useApprovedWhatsAppTemplates();

  const isEdit = !!campaign;

  const { register, control, watch, handleSubmit, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name:          campaign?.name ?? '',
      description:   campaign?.description ?? '',
      type:          campaign?.type ?? 'whatsapp',
      message_body:  campaign?.message_body ?? '',
      template_vars: campaign?.template_vars ?? [],
      scheduled_at:  campaign?.scheduled_at
        ? new Date(campaign.scheduled_at).toISOString().slice(0, 16)
        : null,
      legal_basis:   campaign?.legal_basis ?? 'legitimate_interest',
      aud_activeOnly:   campaign?.audience_filter?.activeOnly ?? true,
      aud_categories:   campaign?.audience_filter?.categories ?? [],
      aud_tags:         campaign?.audience_filter?.tags ?? [],
      aud_neighborhood: campaign?.audience_filter?.neighborhood ?? '',
    },
  });

  const type            = watch('type');
  const messageBody     = watch('message_body') ?? '';
  const templateVars    = watch('template_vars') ?? [];
  const audActive       = watch('aud_activeOnly');
  const audCats         = watch('aud_categories');
  const audTags         = watch('aud_tags');
  const audNeighborhood = watch('aud_neighborhood');

  // Detecta quantas variáveis {{2}}, {{3}},… existem nos templates disponíveis
  const maxExtraVars = waTemplates.reduce((max, tpl) => {
    const matches = tpl.body.match(/\{\{(\d+)\}\}/g) ?? [];
    const highest = matches.reduce((m, v) => Math.max(m, parseInt(v.replace(/\D/g, ''))), 0);
    return Math.max(max, highest - 1); // -1 porque {{1}} é automático
  }, 0);

  const audienceFilter: AudienceFilter = {
    activeOnly:   audActive,
    categories:   audCats?.length ? audCats : undefined,
    tags:         audTags?.length ? audTags : undefined,
    neighborhood: audNeighborhood || undefined,
  };

  const { data: audienceCount = 0, isLoading: countLoading } = useAudiencePreview(audienceFilter);

  const charLimit = type === 'whatsapp' ? WHATSAPP_CHAR_LIMIT : EMAIL_CHAR_LIMIT;
  const charPct   = Math.min(100, (messageBody.length / charLimit) * 100);
  const charColor = charPct > 90 ? 'text-danger' : charPct > 75 ? 'text-amber-400' : 'text-ink-subtle';

  const toggleCategory = (slug: string) => {
    const curr = audCats ?? [];
    setValue('aud_categories', curr.includes(slug) ? curr.filter((s) => s !== slug) : [...curr, slug]);
  };

  const toggleTag = (slug: string) => {
    const curr = audTags ?? [];
    setValue('aud_tags', curr.includes(slug) ? curr.filter((s) => s !== slug) : [...curr, slug]);
  };

  const onValid = (values: FormValues) => {
    const payload: CampaignInsert = {
      name:          values.name,
      description:   values.description || null,
      type:          values.type,
      message_body:  values.message_body,
      template_vars: values.type === 'whatsapp' ? (values.template_vars ?? []) : [],
      scheduled_at:  values.scheduled_at || null,
      legal_basis:   values.legal_basis,
      audience_filter: {
        activeOnly:   values.aud_activeOnly,
        categories:   values.aud_categories?.length ? values.aud_categories : undefined,
        tags:         values.aud_tags?.length ? values.aud_tags : undefined,
        neighborhood: values.aud_neighborhood || undefined,
      },
    };
    onSubmit(payload);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl flex flex-col rounded-2xl glass-strong border border-hairline shadow-deep animate-fade-in max-h-[calc(100vh-2rem)]">

        {/* Header — fixo no topo */}
        <div className="flex items-center justify-between px-6 py-4 hairline-b shrink-0">
          <div>
            <h2 className="font-display text-lg font-bold text-ink">
              {isEdit ? 'Editar Campanha' : 'Nova Campanha'}
            </h2>
            <p className="text-xs text-ink-muted mt-0.5">Configure o disparo e defina o público-alvo</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-ink-subtle hover:text-ink hover:bg-white/5 transition-colors cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Corpo — rolável */}
        <div className="overflow-y-auto flex-1">
          <form id="campaign-form" onSubmit={handleSubmit(onValid)}>
            <div className="p-6 space-y-6">

              {/* Nome + Tipo */}
              <div className="space-y-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-1.5">
                    Nome da campanha *
                  </label>
                  <input
                    {...register('name')}
                    placeholder="Ex: Informativo Semanal — Junho"
                    className="w-full rounded-xl bg-midnight-2 hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                  />
                  {errors.name && <p className="text-danger text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-2">
                    Canal *
                  </label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <div className="grid grid-cols-3 gap-2">
                        {TYPE_OPTIONS.map(({ value, label, icon: Icon, desc }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => field.onChange(value)}
                            className={`flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-all cursor-pointer ${
                              field.value === value
                                ? 'border-accent/50 bg-accent/10 text-accent'
                                : 'border-hairline bg-midnight-2/40 text-ink-muted hover:border-hairline-strong'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-xs font-semibold">{label}</span>
                            <span className="text-[10px] leading-tight opacity-70 hidden sm:block">{desc}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                    Mensagem *
                  </label>
                  <span className={`font-mono text-[10px] ${charColor}`}>
                    {messageBody.length} / {charLimit.toLocaleString('pt-BR')}
                  </span>
                </div>
                <textarea
                  {...register('message_body')}
                  rows={6}
                  placeholder={
                    type === 'whatsapp'
                      ? 'Olá {nome}! Gostaríamos de convidar você para...'
                      : 'Corpo do e-mail...'
                  }
                  className="w-full rounded-xl bg-midnight-2 hairline px-4 py-3 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 resize-none transition-all"
                />
                {/* Barra de progresso de caracteres */}
                <div className="h-0.5 mt-1.5 rounded-full bg-midnight-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${charPct > 90 ? 'bg-danger' : charPct > 75 ? 'bg-amber-400' : 'bg-accent'}`}
                    style={{ width: `${charPct}%` }}
                  />
                </div>
                {type === 'whatsapp' && (
                  <p className="text-[10px] text-ink-subtle mt-1.5 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Use <code className="font-mono bg-midnight-3 px-1 rounded">{'{nome}'}</code> para personalizar com o nome do contato.
                  </p>
                )}
                {errors.message_body && <p className="text-danger text-xs mt-1">{errors.message_body.message}</p>}
              </div>

              {/* Templates WhatsApp aprovados */}
              {type === 'whatsapp' && (
                <div className="rounded-xl border border-hairline overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setTemplatesOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink hover:bg-white/[0.03] transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <LayoutTemplate className="h-4 w-4 text-accent" />
                      <span>Templates aprovados</span>
                      {waTemplates.length > 0 && (
                        <span className="text-[10px] font-mono rounded-full px-2 py-0.5 border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                          {waTemplates.length} disponível{waTemplates.length !== 1 ? 'is' : ''}
                        </span>
                      )}
                    </div>
                    {templatesOpen ? <ChevronUp className="h-4 w-4 text-ink-subtle" /> : <ChevronDown className="h-4 w-4 text-ink-subtle" />}
                  </button>

                  {templatesOpen && (
                    <div className="px-4 pb-4 pt-2 hairline-t space-y-2">
                      {waTemplates.length === 0 ? (
                        <p className="text-xs text-ink-subtle italic py-2 text-center">
                          Nenhum template aprovado. Sincronize no painel do superadmin.
                        </p>
                      ) : (
                        <>
                          <p className="text-[10px] text-ink-subtle mb-3 leading-relaxed">
                            Usado quando o contato não respondeu nas últimas 24h. Clique em
                            <strong className="text-ink mx-1">Usar como base</strong>
                            para pré-preencher a mensagem com o corpo do template.
                          </p>
                          {waTemplates.map((tpl: ApprovedTemplate) => (
                            <div
                              key={tpl.id}
                              className="rounded-xl border border-hairline bg-midnight-2/30 p-3 space-y-2"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-ink font-mono truncate">{tpl.name}</p>
                                  <p className="text-[10px] text-ink-subtle">
                                    {tpl.language} · {USE_CASE_LABELS[tpl.use_case] ?? tpl.use_case}
                                    {tpl.button_labels?.length > 0 && (
                                      <span className="ml-2 text-accent">
                                        [{tpl.button_labels.join(' / ')}]
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setValue('message_body', tpl.body);
                                    setAppliedTpl(tpl.id);
                                    setTemplatesOpen(false);
                                  }}
                                  className="shrink-0 flex items-center gap-1 text-[10px] font-semibold rounded-lg px-2.5 py-1.5 border border-accent/30 text-accent bg-accent/10 hover:bg-accent/20 transition-colors cursor-pointer"
                                >
                                  {appliedTpl === tpl.id
                                    ? <><CopyCheck className="h-3 w-3" /> Aplicado</>
                                    : 'Usar como base'
                                  }
                                </button>
                              </div>
                              <p className="text-[11px] text-ink-muted bg-midnight-3/50 rounded-lg px-3 py-2 whitespace-pre-wrap leading-relaxed line-clamp-3">
                                {tpl.body}
                              </p>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Variáveis extras do template ({{2}}, {{3}}, …) */}
              {type === 'whatsapp' && maxExtraVars > 0 && (
                <div className="rounded-xl border border-accent/20 bg-accent/5 p-4 space-y-3">
                  <p className="text-xs font-bold text-ink flex items-center gap-1.5">
                    <Info className="h-3.5 w-3.5 text-accent" />
                    Variáveis do template
                    <span className="text-[10px] font-normal text-ink-subtle ml-1">
                      — preenchidas iguais para todos os contatos da campanha
                    </span>
                  </p>
                  <p className="text-[10px] text-ink-subtle leading-relaxed">
                    <code className="bg-midnight-3 px-1 rounded font-mono">{'{{1}}'}</code> = nome do contato (automático).
                    Preencha abaixo as variáveis seguintes usadas nos templates aprovados.
                  </p>
                  <div className="space-y-2">
                    {Array.from({ length: maxExtraVars }, (_, i) => (
                      <div key={i}>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.12em] text-ink-subtle mb-1">
                          {`{{${i + 2}}}`}
                          {i === 0 && waTemplates.some(t => /\{\{2\}\}/.test(t.body)) && (
                            <span className="ml-2 normal-case font-sans font-normal">
                              {waTemplates.find(t => /\{\{2\}\}/.test(t.body))?.use_case === 'event_confirm'
                                ? '— ex: "15 de junho", "nome do evento"'
                                : '— ex: "30 dias", slogan'}
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={templateVars[i] ?? ''}
                          onChange={(e) => {
                            const updated = [...(templateVars ?? [])];
                            updated[i] = e.target.value;
                            setValue('template_vars', updated);
                          }}
                          placeholder={`Valor de {{${i + 2}}} enviado a todos os contatos`}
                          className="w-full rounded-xl bg-midnight-2 hairline px-3 py-2.5 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audiência */}
              <div className="rounded-xl border border-hairline overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAudienceOpen((o) => !o)}
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink hover:bg-white/[0.03] transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-accent" />
                    <span>Público-alvo</span>
                    <span className={`font-mono text-[10px] rounded-full px-2 py-0.5 border ${
                      countLoading ? 'text-ink-subtle border-hairline' : 'text-accent border-accent/30 bg-accent/10'
                    }`}>
                      {countLoading ? '...' : `${audienceCount.toLocaleString('pt-BR')} contatos`}
                    </span>
                  </div>
                  {audienceOpen ? <ChevronUp className="h-4 w-4 text-ink-subtle" /> : <ChevronDown className="h-4 w-4 text-ink-subtle" />}
                </button>

                {audienceOpen && (
                  <div className="px-4 pb-4 pt-2 hairline-t space-y-4">
                    {/* Apenas ativos */}
                    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                      <Controller
                        name="aud_activeOnly"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className="h-4 w-4 rounded accent-accent"
                          />
                        )}
                      />
                      Apenas contatos ativos
                    </label>

                    {/* Categorias */}
                    {categories.length > 0 && (
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-2">
                          Filtrar por categoria <span className="normal-case">(vazio = todas)</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {categories.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => toggleCategory(cat.slug)}
                              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-all cursor-pointer ${
                                (audCats ?? []).includes(cat.slug)
                                  ? 'border-accent/50 bg-accent/15 text-accent'
                                  : 'border-hairline text-ink-muted hover:border-hairline-strong'
                              }`}
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {allTags.length > 0 && (
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-2">
                          Filtrar por tag <span className="normal-case">(vazio = todas)</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {allTags.map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => toggleTag(tag.slug)}
                              className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-all cursor-pointer ${
                                (audTags ?? []).includes(tag.slug)
                                  ? `${tag.color_class}`
                                  : 'border-hairline text-ink-muted hover:border-hairline-strong'
                              }`}
                            >
                              {tag.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bairro */}
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-1.5">
                        Filtrar por bairro <span className="normal-case">(opcional)</span>
                      </label>
                      <input
                        {...register('aud_neighborhood')}
                        placeholder="Ex: Centro, Vila Nova..."
                        className="w-full rounded-xl bg-midnight-2 hairline px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Agendamento */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-1.5">
                  Agendar envio <span className="normal-case">(opcional — deixe vazio para salvar como rascunho)</span>
                </label>
                <input
                  {...register('scheduled_at')}
                  type="datetime-local"
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full rounded-xl bg-midnight-2 hairline px-4 py-2.5 text-sm text-ink outline-none focus:border-accent/40 transition-all"
                />
              </div>

              {/* Base legal */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle mb-1.5">
                  Base legal (LGPD) *
                </label>
                <select
                  {...register('legal_basis')}
                  className="w-full rounded-xl bg-midnight-2 hairline px-4 py-2.5 text-sm text-ink outline-none focus:border-accent/40 transition-all"
                >
                  <option value="legitimate_interest">Legítimo interesse</option>
                  <option value="consent">Consentimento do titular</option>
                  <option value="contract">Execução de contrato</option>
                </select>
              </div>
            </div>
          </form>
        </div>

        {/* Footer — fixo na base */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 hairline-t glass-strong shrink-0 rounded-b-2xl">
          <div className="text-xs text-ink-subtle">
            {audienceCount > 0
              ? <><span className="font-semibold text-accent">{audienceCount.toLocaleString('pt-BR')}</span> contatos alcançados</>
              : 'Defina o público-alvo acima'}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl btn-ghost px-4 py-2.5 text-sm font-semibold cursor-pointer"
            >
              Cancelar
            </button>
            <button
              form="campaign-form"
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 rounded-xl btn-accent px-5 py-2.5 text-sm font-semibold disabled:opacity-50 cursor-pointer"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Salvar alterações' : 'Criar campanha'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
